import {
  invalidateTovQuery,
  useTovMutation,
  useTovQuery,
  useTovQueryClient,
} from '@tov-plus/frontend-data';
import { useEffect, useMemo, useState } from 'react';

import { guestApi } from '../../app/api';
import type { useGuestAccess } from '../../contexts/GuestAccessContext';
import type {
  GuestMessagingInboxItem,
  GuestRecentCommunication,
} from '../api/guestApi';
import { guestQueryKeys } from '../data/guestQueryKeys';

function getGuestLiveUpdatesStorageKey(
  occasionSlug: string,
  guestToken: string,
) {
  return `tov-plus:guest-live-updates:${occasionSlug}:${guestToken}`;
}

type GuestAccessValue = ReturnType<typeof useGuestAccess>;

function getLatestMessageId(item: GuestMessagingInboxItem) {
  return item.latestMessage?.id ?? null;
}

export function getUnreadMessagingThreads(inbox: GuestMessagingInboxItem[]) {
  return inbox.filter((item) => item.unreadCount > 0);
}

export function normalizeReadDeliveryIds(value: unknown) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string');
}

export function getUnreadCommunicationsFromInbox(
  inbox: GuestMessagingInboxItem[],
  recentCommunications: GuestRecentCommunication[],
) {
  const unreadThreads = getUnreadMessagingThreads(inbox);
  const unreadEventIds = new Set(
    unreadThreads
      .map((item) => item.thread.eventId)
      .filter((eventId): eventId is string => Boolean(eventId)),
  );
  const unreadLatestMessageIds = new Set(
    unreadThreads
      .map(getLatestMessageId)
      .filter((messageId): messageId is string => Boolean(messageId)),
  );

  return recentCommunications.filter((item) => {
    return (
      (item.event && unreadEventIds.has(item.event.id)) ||
      (item.message && unreadLatestMessageIds.has(item.message.id))
    );
  });
}

export function countUnreadMessagesFromInbox(inbox: GuestMessagingInboxItem[]) {
  return getUnreadMessagingThreads(inbox).reduce(
    (total, item) => total + item.unreadCount,
    0,
  );
}

export function findUnreadThreadForCommunication(
  inbox: GuestMessagingInboxItem[],
  communication: GuestRecentCommunication | undefined,
) {
  return getUnreadMessagingThreads(inbox).find((item) => {
    return (
      (communication?.event &&
        item.thread.eventId === communication.event.id) ||
      (communication?.message &&
        getLatestMessageId(item) === communication.message.id)
    );
  });
}

export function useGuestLiveUpdates(access: GuestAccessValue) {
  const [readDeliveryIds, setReadDeliveryIds] = useState<string[]>([]);
  const queryClient = useTovQueryClient();
  const readyOccasionSlug =
    access.status === 'ready' ? access.occasionSlug : '';
  const readyGuestToken = access.status === 'ready' ? access.guestToken : '';

  const messagingInboxQuery = useTovQuery({
    queryKey:
      access.status === 'ready'
        ? guestQueryKeys.messagingInbox(readyOccasionSlug, readyGuestToken)
        : guestQueryKeys.messagingInbox('unknown-occasion', 'unknown-token'),
    enabled: access.status === 'ready',
    queryFn: () => {
      return guestApi.getMessagingInbox(readyOccasionSlug, readyGuestToken);
    },
  });

  const markThreadReadMutation = useTovMutation({
    mutationFn: (input: {
      occasionSlug: string;
      guestToken: string;
      threadId: string;
      latestMessageId: string | null;
    }) => {
      return guestApi.postMessagingThreadRead(
        input.occasionSlug,
        input.guestToken,
        input.threadId,
        input.latestMessageId,
      );
    },
    onSuccess: (_result, input) => {
      void invalidateTovQuery(
        queryClient,
        guestQueryKeys.messagingInbox(input.occasionSlug, input.guestToken),
      );
      void invalidateTovQuery(
        queryClient,
        guestQueryKeys.bootstrap(input.occasionSlug, input.guestToken),
      );
      access.refresh();
    },
  });

  useEffect(() => {
    if (access.status !== 'ready') {
      return;
    }

    const stored = window.localStorage.getItem(
      getGuestLiveUpdatesStorageKey(access.occasionSlug, access.guestToken),
    );

    if (!stored) {
      setReadDeliveryIds([]);
      return;
    }

    try {
      setReadDeliveryIds(normalizeReadDeliveryIds(JSON.parse(stored)));
    } catch {
      window.localStorage.removeItem(
        getGuestLiveUpdatesStorageKey(access.occasionSlug, access.guestToken),
      );
      setReadDeliveryIds([]);
    }
  }, [access]);

  useEffect(() => {
    if (access.status !== 'ready') {
      return;
    }

    window.localStorage.setItem(
      getGuestLiveUpdatesStorageKey(access.occasionSlug, access.guestToken),
      JSON.stringify(readDeliveryIds),
    );
  }, [access, readDeliveryIds]);

  const unreadItems = useMemo(() => {
    if (access.status !== 'ready') {
      return [];
    }

    const inbox = messagingInboxQuery.data;
    if (inbox) {
      return getUnreadCommunicationsFromInbox(
        inbox,
        access.experience.recentCommunications,
      );
    }

    const readIds = new Set(readDeliveryIds);

    return access.experience.recentCommunications.filter(
      (item) => !readIds.has(item.delivery.id),
    );
  }, [access, messagingInboxQuery.data, readDeliveryIds]);

  const unreadCount = useMemo(() => {
    const inbox = messagingInboxQuery.data;
    if (inbox) {
      return countUnreadMessagesFromInbox(inbox);
    }

    return unreadItems.length;
  }, [messagingInboxQuery.data, unreadItems.length]);

  return {
    unreadItems,
    unreadCount,
    hasUnreadEventUpdate(eventId: string) {
      const inbox = messagingInboxQuery.data;
      if (inbox) {
        return getUnreadMessagingThreads(inbox).some(
          (item) => item.thread.eventId === eventId,
        );
      }

      return unreadItems.some((item) => item.event?.id === eventId);
    },
    markRead(deliveryId: string) {
      if (access.status === 'ready' && messagingInboxQuery.data) {
        const communication = access.experience.recentCommunications.find(
          (item) => item.delivery.id === deliveryId,
        );
        const thread = findUnreadThreadForCommunication(
          messagingInboxQuery.data,
          communication,
        );

        if (thread) {
          markThreadReadMutation.mutate({
            occasionSlug: access.occasionSlug,
            guestToken: access.guestToken,
            threadId: thread.thread.id,
            latestMessageId: getLatestMessageId(thread),
          });
          return;
        }
      }

      setReadDeliveryIds((current) =>
        current.includes(deliveryId) ? current : [...current, deliveryId],
      );
    },
    markAllRead() {
      if (access.status !== 'ready') {
        return;
      }

      const inbox = messagingInboxQuery.data;
      if (inbox) {
        for (const item of getUnreadMessagingThreads(inbox)) {
          markThreadReadMutation.mutate({
            occasionSlug: access.occasionSlug,
            guestToken: access.guestToken,
            threadId: item.thread.id,
            latestMessageId: getLatestMessageId(item),
          });
        }
        return;
      }

      setReadDeliveryIds(
        access.experience.recentCommunications.map((item) => item.delivery.id),
      );
    },
  };
}
